import * as z from "zod";
import { type ManifestValidationResult, validateManifest } from "./_shared.js";

export const McpServerManifestSchema = z.looseObject({
  name: z.string().min(1),
  description: z.string().min(1),
  version: z.union([z.string(), z.number()]).optional(),
  // "http" is the runtime literal the runner honours (StreamableHTTPClientTransport);
  // "streamable-http" is accepted as an MCP-spec-aligned alias and normalized to
  // "http" in mcpDeclFromCatalogEntry. Keep both until the alias is retired.
  transport: z.enum(["stdio", "sse", "http", "streamable-http"]).optional(),
  command: z.string().optional(),
  args: z.array(z.string()).optional(),
  env: z.record(z.string(), z.string()).optional(),
  url: z.string().optional(),
  headers: z.record(z.string(), z.string()).optional(),
  keywords: z.array(z.string()).optional(),
  license: z.string().optional(),
  // Optional runnable payload fetched + sha256-verified by the platform
  // session-materializer into the asset dir at materialize time (e.g. a bundled
  // server.js shipped as a GitHub release asset). `dest` defaults to the URL
  // basename.
  payload: z
    .object({
      // https only — bare `z.string().url()` also accepts file:/data:/javascript: schemes.
      url: z
        .string()
        .url()
        .refine((u) => u.startsWith("https://"), { message: "payload url must be https" }),
      sha256: z.string().regex(/^[a-f0-9]{64}$/),
      // Plain filename, no path separators — schema-level guard against a traversal dest.
      dest: z
        .string()
        .regex(/^[^/\\]+$/, { message: "dest must be a plain filename, not a path" })
        .optional(),
    })
    .optional(),
});

export type McpServerManifest = z.infer<typeof McpServerManifestSchema>;

export function validateMcpServer(data: unknown): ManifestValidationResult<McpServerManifest> {
  return validateManifest(McpServerManifestSchema, data);
}
